import React, { useId, useState } from 'react';

import { CustomInput } from '../../../components';
import { clampQty, MAX_BOXES } from './lines';
import styles from './styles.module.css';

export interface LineStepperProps {
  lineNumber: number;
  qty: number;
  /** Largest qty this line may take: its qty plus the shipment's remaining capacity. */
  max: number;
  /** Fewer/more: a step from the live qty, since `qty` can lag a render behind; the owner stops at the limits. */
  onStep: (delta: 1 | -1) => void;
  /** The typed count on blur or Enter (the owner clamps it), or a typed count plus a step, clamped to 1..max. */
  onCommit: (qty: number) => void;
}

const keepFocus = (e: React.MouseEvent): void => e.preventDefault();

/** Number-of-boxes control: fewer / editable input (digits only, commits on blur and Enter) / more. Limits are aria-disabled so focus stays put, and follow the count being typed. */
export const LineStepper: React.FC<LineStepperProps> = ({
  lineNumber,
  qty,
  max,
  onStep,
  onCommit,
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const hintId = useId();
  const pending = draft !== null && draft !== '' ? Number(draft) : null;
  const shown = pending ?? qty;

  const commit = (): void => {
    if (draft === null) return;
    setDraft(null);
    if (pending !== null) onCommit(pending);
  };

  // A count still being typed goes with the step as one change, clamped to the limits: 999 then − is max, 0 then + is 1.
  const step = (delta: 1 | -1): void => {
    setDraft(null);
    if (pending !== null) onCommit(clampQty(pending + delta, max));
    else onStep(delta);
  };

  return (
    <>
      <div className={styles.stepper}>
        <button
          type='button'
          className={styles.stepperButton}
          aria-label='Fewer boxes'
          aria-disabled={shown <= 1}
          onMouseDown={keepFocus}
          onClick={() => step(-1)}
        >
          −
        </button>
        <CustomInput
          aria-label={`Number of boxes in line ${lineNumber}`}
          inputMode='numeric'
          value={draft ?? String(qty)}
          onChange={(e) => {
            if (/^\d{0,3}$/.test(e.target.value)) setDraft(e.target.value);
          }}
          onBlur={commit}
          onPressEnter={commit}
          height='36px'
          bgColor='transparent'
          borderRadius='0'
          className={styles.stepperCount}
        />
        <button
          type='button'
          className={styles.stepperButton}
          aria-label='More boxes'
          aria-disabled={shown >= max}
          aria-describedby={shown >= max ? hintId : undefined}
          onMouseDown={keepFocus}
          onClick={() => step(1)}
        >
          +
        </button>
      </div>
      {shown >= max && (
        <div id={hintId} className={styles.boxHint} role='status'>
          Up to {MAX_BOXES} boxes per shipment.
        </div>
      )}
    </>
  );
};
